/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import Currency from "currency.js"
import {
  getCurrencyCodeByCountryCode,
  getLocaleInfo,
} from "common/utils/locale"
import { storageHelper } from "./storage"

interface CurrencyInfo {
  code: string
  decimal: number
  separator: string
  symbol: string
  decimalSymbol: string
  exponent: number
}

export function getCurrencyInfo(currencyCode?: string): CurrencyInfo {
  const localeInfo = getLocaleInfo(currencyCode ?? "")
  return {
    code: localeInfo.currencyCode,
    decimal: localeInfo.decimals,
    exponent: localeInfo.exponentUnit,
    separator: localeInfo.thousandSymbol,
    symbol: localeInfo.currencySymbol,
    decimalSymbol: localeInfo.decimalSymbol,
  }
}

const defaultExponent = 2

export const minorUnitToCurrency = (value?: number, currency?: string) => {
  const exponent = getCurrencyInfo(currency ?? "").exponent ?? defaultExponent

  return Number(value ?? 0) / 10 ** exponent
}

export const currencyToMinorUnit = (
  value?: string | number,
  currency?: string,
) => {
  const exponent = getCurrencyInfo(currency).exponent ?? defaultExponent

  return parseFloat((Number(value ?? 0) * 10 ** exponent).toFixed(0))
}

export const currencyToCentUnit = (value?: string | number) =>
  parseFloat((Number(value ?? 0) * 100).toFixed(0))

export const currencyHasDecimalSupport = (currency?: string) =>
  getCurrencyInfo(currency ?? "")?.decimal > 0

const currencyCode = getCurrencyCodeByCountryCode(
  storageHelper.getUserCountry() || "SG",
)
const currencyInfo = getCurrencyInfo(currencyCode)

// IDR -> 1000 -> 1.000
// SGD -> 1,01 -> 1.01
export const getAmountWithoutCurrencySymbol = (
  amount: number,
  precision?: number,
) => {
  return new Currency(amount, {
    symbol: currencyInfo.symbol,
    separator: currencyInfo.separator,
    precision: precision ?? currencyInfo.decimal,
    pattern: "#",
  }).format()
}

// IDR -> 1000 -> Rp1.000
// SGD -> 1,01 -> S$1.01
export const getAmountWithCurrencySymbol = (
  amount: number,
  precision?: number,
) => {
  return new Currency(amount, {
    symbol: currencyInfo.symbol,
    separator: currencyInfo.separator,
    precision: precision ?? currencyInfo.decimal,
  }).format()
}
